import { useQuery, ApolloError } from '@apollo/client';
import { PIVOT_QUERY, PivotQueryVariables, PivotQueryResult } from './graphql';

export interface UsePivotQueryResult {
  data?: PivotQueryResult;
  loading: boolean;
  error?: ApolloError;
  networkStatus: number;
  isBusy: boolean;
  refetch: (variables?: PivotQueryVariables) => Promise<any>;
}

export interface UsePivotQueryOptions {
  skip?: boolean;
  isDragging?: boolean;
  variables?: PivotQueryVariables;
  onError?: (error: ApolloError) => void;
  onCompleted?: (data: PivotQueryResult) => void;
  enabled?: boolean;
}

export function usePivotQuery({
  skip = false,
  isDragging = false,
  variables,
  onError,
  onCompleted,
  enabled = true,
}: UsePivotQueryOptions = {}): UsePivotQueryResult {
  // Skip queries during drag to avoid thrash or when conditions not met
  const shouldSkip = skip || isDragging || !enabled;

  const { data, loading, error, refetch, networkStatus } = useQuery<
    PivotQueryResult,
    PivotQueryVariables
  >(PIVOT_QUERY, {
    variables,
    skip: shouldSkip,
    onError,
    onCompleted,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Better for drag-drop scenarios
    notifyOnNetworkStatusChange: true, // Enable networkStatus tracking
  });

  // Apollo networkStatus:
  // 1=loading, 2=setVariables, 3=fetchMore, 4=refetch, 6=poll, 7=ready, 8=error.
  // Keep DnD disabled only while an active request is in progress.
  const isTransitionalStatus =
    networkStatus === 1 || networkStatus === 2 || networkStatus === 3 || networkStatus === 4;
  const isBusy = shouldSkip ? false : loading || isTransitionalStatus;

  return {
    data,
    loading,
    error,
    networkStatus,
    isBusy,
    refetch, // Expose Apollo's refetch directly
  };
}
